import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import InputNumberFormat from 'react-number-format';

import { yupResolver } from '@hookform/resolvers/yup';
import { Eye, Plus, Search, X } from 'lucide-react';
import * as yup from 'yup';
import {
  Box,
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputRightAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
} from '@chakra-ui/react';

import OwnSelect, { SelectedValue } from 'components/OwnSelect';
import { ModalRootProps } from 'components/Modal/Root';
import useModal from 'hooks/useModal';
import {
  OpportunityFormData,
  mapFormDataToOpportunityPayload,
  mapOpportunityToFormData,
  opportunityFormDefaultValues,
} from 'pages/Opportunity/form';
import {
  useCreateOpportunity,
  useOpportunity,
  useOpportunityOrigins,
  useOrganizationUsers,
  useUpdateOpportunity,
} from 'pages/Opportunity/queries';
import { getOpportunityClientName } from 'pages/Opportunity/utils';
import { api } from 'services/api';
import { toast } from 'shared/toast';
import { CEPServiceResponse } from 'types/address';
import { Client } from 'types/client';
import { Opportunity } from 'types/opportunity';

type OpportunityFormModalProps = ModalRootProps & {
  data?: { opportunityId?: number; duplicateFromId?: number };
  onConfirm?: (opportunity: Opportunity) => void;
};

type CepStatus = 'idle' | 'loading' | 'found' | 'error';

const cepHelperMessages: Record<CepStatus, { message: string; color: string }> =
  {
    idle: {
      message: 'Digite o CEP para preencher o endereço.',
      color: 'gray.500',
    },
    loading: { message: 'Buscando endereço…', color: 'gray.500' },
    found: {
      message: 'Endereço preenchido. Confira e informe o número.',
      color: 'green.600',
    },
    error: {
      message: 'CEP não encontrado. Preencha o endereço.',
      color: 'red.600',
    },
  };

const validationSchema = yup.object().shape({
  description: yup.string().trim().required('Informe o nome da oportunidade.'),
  origin_id: yup.number().nullable().required('Selecione o canal de origem.'),
  responsible_id: yup
    .number()
    .nullable()
    .required('Selecione o responsável interno.'),
  service_type: yup
    .string()
    .trim()
    .required('Informe o tipo de obra / serviço.')
    .max(255, 'O tipo de obra / serviço deve ter no máximo 255 caracteres.'),
  opportunity_date: yup.string().required('Informe o prazo previsto de envio.'),
  estimated_value: yup
    .number()
    .nullable()
    .min(0, 'O valor estimado não pode ser negativo.'),
  address: yup
    .string()
    .max(255, 'O endereço deve ter no máximo 255 caracteres.'),
  number: yup.string().max(20, 'O número deve ter no máximo 20 caracteres.'),
  complement: yup
    .string()
    .max(255, 'O complemento deve ter no máximo 255 caracteres.'),
  district: yup
    .string()
    .max(255, 'O bairro deve ter no máximo 255 caracteres.'),
});

const OpportunityFormModal: React.FC<OpportunityFormModalProps> = ({
  data,
  handleClose,
  onConfirm,
  ...modalProps
}) => {
  const { openModal } = useModal();
  const opportunityId = data?.opportunityId;
  const isEditing = Boolean(opportunityId);
  const duplicateFromId = isEditing ? undefined : data?.duplicateFromId;
  const isDuplicating = Boolean(duplicateFromId);
  const [cepStatus, setCepStatus] = useState<CepStatus>('idle');

  const opportunity = useOpportunity(opportunityId ?? duplicateFromId);
  const origins = useOpportunityOrigins();
  const users = useOrganizationUsers();
  const createOpportunity = useCreateOpportunity();
  const updateOpportunity = useUpdateOpportunity();
  const saving = createOpportunity.isLoading || updateOpportunity.isLoading;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    control,
    watch,
    formState: { errors },
  } = useForm<OpportunityFormData>({
    defaultValues: opportunityFormDefaultValues,
    resolver: yupResolver(
      validationSchema,
    ) as unknown as Resolver<OpportunityFormData>,
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (!opportunity.data) return;

    const formData = mapOpportunityToFormData(opportunity.data);

    reset(
      isDuplicating
        ? { ...formData, description: `${formData.description} — Cópia` }
        : formData,
    );
  }, [isDuplicating, opportunity.data, reset]);

  useEffect(() => {
    if (opportunity.isError) {
      toast({
        status: 'error',
        description: 'Houve um erro ao tentar carregar a oportunidade.',
      });
      handleClose();
    }
  }, [handleClose, opportunity.isError]);

  const originOptions = useMemo<SelectedValue[]>(
    () =>
      (origins.data || []).map((origin) => ({
        value: String(origin.id),
        label: origin.description,
      })),
    [origins.data],
  );

  const responsibleOptions = useMemo<SelectedValue[]>(() => {
    const options = (users.data || []).map(({ user: organizationUser }) => ({
      value: String(organizationUser.id),
      label: organizationUser.name || organizationUser.email,
    }));
    const currentResponsible = opportunity.data?.responsible;

    // Mantém o responsável atual visível mesmo que não esteja mais ativo.
    if (
      currentResponsible?.id &&
      !options.some((option) => option.value === String(currentResponsible.id))
    ) {
      options.push({
        value: String(currentResponsible.id),
        label: currentResponsible.name || currentResponsible.email,
      });
    }

    return options;
  }, [opportunity.data?.responsible, users.data]);

  const clientId = watch('client_id');
  const clientName = watch('client_name');
  const cityName = watch('city_name');

  const selectClient = useCallback(
    (client: Client): void => {
      setValue('client_id', client.id);
      setValue('client_name', getOpportunityClientName(client));
    },
    [setValue],
  );

  const removeClient = (): void => {
    setValue('client_id', null);
    setValue('client_name', '');
  };

  const findCep = useCallback(
    async (cep: string): Promise<void> => {
      setCepStatus('loading');
      try {
        const { data: response } = await api.get<CEPServiceResponse>(
          `/zipcode/${cep}`,
        );

        // Ignora respostas de um CEP que já foi alterado.
        if (getValues('zip_code') !== cep) return;

        const { data: address } = response;
        const { city } = address;

        setValue('city_id', city?.id ?? null);
        setValue(
          'city_name',
          [city?.name, city?.state?.abbreviation].filter(Boolean).join('/'),
        );
        setValue('address', address.street || '');
        setValue('district', address.neighborhood || '');
        setValue('complement', address.complement || '');
        setCepStatus('found');
      } catch {
        if (getValues('zip_code') === cep) setCepStatus('error');
      }
    },
    [getValues, setValue],
  );

  const cepHelper = cepHelperMessages[cepStatus];

  const submit = async (formData: OpportunityFormData): Promise<void> => {
    if (saving) return;

    // A cópia nasce sem valor de proposta; a etapa inicial é definida pela API.
    const payload = mapFormDataToOpportunityPayload(
      formData,
      isEditing ? opportunity.data : undefined,
    );

    try {
      const saved =
        isEditing && opportunityId
          ? await updateOpportunity.mutateAsync({ id: opportunityId, payload })
          : await createOpportunity.mutateAsync(payload);

      let successMessage = 'Oportunidade criada com sucesso.';
      if (isEditing) successMessage = 'Oportunidade atualizada com sucesso.';
      if (isDuplicating) successMessage = 'Oportunidade duplicada com sucesso.';

      toast({ status: 'success', description: successMessage });
      onConfirm?.(saved);
      handleClose();
    } catch (err) {
      toast({
        status: 'error',
        description:
          err.response?.data?.message ||
          (isEditing
            ? 'Houve um erro ao tentar atualizar a oportunidade.'
            : 'Houve um erro ao tentar criar a oportunidade.'),
      });
    }
  };

  const renderSelect = (
    name: 'origin_id' | 'responsible_id',
    options: SelectedValue[],
    isLoading: boolean,
  ): React.ReactNode => (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => (
        <OwnSelect
          placeholder={isLoading ? 'Carregando' : 'Selecione'}
          options={options}
          value={options.find((option) => option.value === String(value))}
          isInvalid={!!errors[name]}
          isLoading={isLoading}
          isDisabled={isLoading}
          isSearchable
          noOptionsMessage={() => 'Nenhum resultado encontrado'}
          onChange={(selected: SelectedValue | null) =>
            onChange(selected ? Number(selected.value) : null)
          }
        />
      )}
    />
  );

  return (
    <Modal
      {...modalProps}
      onClose={handleClose}
      size="3xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent maxH="calc(100vh - 3rem)">
        <ModalHeader>
          {isEditing && 'Editar oportunidade'}
          {isDuplicating && 'Duplicar oportunidade'}
          {!isEditing && !isDuplicating && 'Criar oportunidade'}
          <Text fontSize="sm" fontWeight="normal" color="gray.500">
            {isEditing
              ? `Atualize os dados de ${opportunity.data?.description || 'oportunidade'}.`
              : 'A oportunidade será criada na etapa inicial do funil.'}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        {(isEditing || isDuplicating) && opportunity.isLoading ? (
          <ModalBody>
            <Center py={10}>
              <Spinner />
            </Center>
          </ModalBody>
        ) : (
          <ModalBody
            as="form"
            id="opportunity-form"
            // `isRequired` coloca `required` nos inputs; a validação fica com o yup.
            noValidate
            onSubmit={handleSubmit(submit)}
          >
            <Box mb={6}>
              <Grid
                templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
                gap={4}
              >
                <FormControl
                  gridColumn={{ md: 'span 2' }}
                  isInvalid={!!errors.description}
                  isRequired
                >
                  <FormLabel>Nome da oportunidade / obra</FormLabel>
                  <Input
                    {...register('description')}
                    placeholder="Ex.: Reforma da área de lazer"
                  />
                  <FormErrorMessage>
                    {errors.description?.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors.origin_id} isRequired>
                  <FormLabel>Canal de origem</FormLabel>
                  {renderSelect('origin_id', originOptions, origins.isLoading)}
                  <FormErrorMessage>
                    {errors.origin_id?.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors.responsible_id} isRequired>
                  <FormLabel>Responsável interno</FormLabel>
                  {renderSelect(
                    'responsible_id',
                    responsibleOptions,
                    users.isLoading,
                  )}
                  <FormErrorMessage>
                    {errors.responsible_id?.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors.service_type} isRequired>
                  <FormLabel>Tipo de obra / serviço</FormLabel>
                  <Input
                    {...register('service_type')}
                    placeholder="Ex.: Reforma comercial"
                  />
                  <FormErrorMessage>
                    {errors.service_type?.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors.opportunity_date} isRequired>
                  <FormLabel>Prazo previsto de envio</FormLabel>
                  <Input type="date" {...register('opportunity_date')} />
                  <FormErrorMessage>
                    {errors.opportunity_date?.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl
                  gridColumn={{ md: 'span 2' }}
                  isInvalid={!!errors.estimated_value}
                >
                  <FormLabel>Valor estimado inicial</FormLabel>
                  <Controller
                    name="estimated_value"
                    control={control}
                    render={({ field: { onChange, value, ...field } }) => (
                      <InputNumberFormat
                        {...field}
                        value={value ?? ''}
                        isNumericString
                        customInput={Input}
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        allowNegative={false}
                        prefix="R$ "
                        placeholder="R$ 0,00"
                        onFocus={(e) => {
                          e.target.select();
                        }}
                        onValueChange={(values) =>
                          onChange(values.floatValue ?? null)
                        }
                      />
                    )}
                  />
                  <FormErrorMessage>
                    {errors.estimated_value?.message}
                  </FormErrorMessage>
                </FormControl>
              </Grid>
            </Box>
            <Box mb={6}>
              <FormControl>
                <FormLabel>Cliente / Empresa</FormLabel>
                <InputGroup>
                  <Input
                    value={clientName}
                    placeholder="Nenhum cliente selecionado"
                    isReadOnly
                    _readOnly={{ backgroundColor: 'white' }}
                  />
                  <InputRightAddon px={0}>
                    <IconButton
                      aria-label="Pesquisar cliente"
                      title="Pesquisar cliente"
                      height="100%"
                      width="50px"
                      colorScheme="green"
                      borderRadius="0"
                      icon={<Search size={18} />}
                      onClick={() =>
                        openModal('clientLookup', {
                          mode: 'lookup',
                          onConfirm: selectClient,
                        })
                      }
                    />
                  </InputRightAddon>
                  {clientId && (
                    <>
                      <InputRightAddon px={0}>
                        <IconButton
                          aria-label="Visualizar dados do cliente"
                          title="Visualizar dados do cliente"
                          height="100%"
                          width="50px"
                          colorScheme="blue"
                          borderRadius="0"
                          icon={<Eye size={18} />}
                          onClick={() =>
                            openModal('clientSelfLookup', {
                              mode: 'lookup',
                              data: { client_id: clientId },
                            })
                          }
                        />
                      </InputRightAddon>
                      <InputRightAddon px={0}>
                        <IconButton
                          aria-label="Remover cliente"
                          title="Remover cliente"
                          height="100%"
                          width="50px"
                          colorScheme="red"
                          borderRadius="0"
                          icon={<X size={18} />}
                          onClick={removeClient}
                        />
                      </InputRightAddon>
                    </>
                  )}
                  <InputRightAddon px={0}>
                    <IconButton
                      aria-label="Criar cliente"
                      title="Criar cliente"
                      height="100%"
                      width="50px"
                      colorScheme="blue"
                      borderTopLeftRadius="0"
                      borderBottomLeftRadius="0"
                      icon={<Plus size={18} />}
                      onClick={() =>
                        openModal('clientAdd', {
                          mode: 'add',
                          onConfirm: selectClient,
                        })
                      }
                    />
                  </InputRightAddon>
                </InputGroup>
              </FormControl>
            </Box>
            <Box mb={6}>
              <Grid
                templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
                gap={4}
              >
                <FormControl>
                  <FormLabel>CEP da obra</FormLabel>
                  <Controller
                    name="zip_code"
                    control={control}
                    render={({ field }) => (
                      <InputNumberFormat
                        {...field}
                        customInput={Input}
                        type="tel"
                        format="#####-###"
                        mask="_"
                        placeholder="00000-000"
                        onValueChange={(value) =>
                          field.onChange(value.formattedValue)
                        }
                        // onChange só dispara na digitação; onValueChange também
                        // dispara ao carregar a edição e sobrescreveria o endereço.
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          const cep = event.target.value;

                          if (cep.replace(/\D/g, '').length === 8) {
                            findCep(cep);
                          } else {
                            setCepStatus('idle');
                          }
                        }}
                      />
                    )}
                  />
                  <FormHelperText
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                    color={cepHelper.color}
                  >
                    {cepStatus === 'loading' && <Spinner size="xs" />}
                    {cepHelper.message}
                  </FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel>Cidade/UF</FormLabel>
                  <Input
                    value={cityName}
                    placeholder="Preenchida pela busca de CEP"
                    isReadOnly
                    _readOnly={{
                      backgroundColor: '#fff',
                    }}
                  />
                </FormControl>

                <FormControl isInvalid={!!errors.address}>
                  <FormLabel>Endereço da obra</FormLabel>
                  <Input {...register('address')} />
                  <FormErrorMessage>{errors.address?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.district}>
                  <FormLabel>Bairro</FormLabel>
                  <Input {...register('district')} />
                  <FormErrorMessage>
                    {errors.district?.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors.number}>
                  <FormLabel>Número</FormLabel>
                  <Input {...register('number')} />
                  <FormErrorMessage>{errors.number?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.complement}>
                  <FormLabel>Complemento</FormLabel>
                  <Input {...register('complement')} />
                  <FormErrorMessage>
                    {errors.complement?.message}
                  </FormErrorMessage>
                </FormControl>
              </Grid>
            </Box>
            <Box>
              <Heading size="sm" mb={4}>
                Observações
              </Heading>
              <FormControl>
                <Textarea
                  {...register('observation')}
                  placeholder="Informações relevantes sobre a demanda do cliente"
                />
              </FormControl>
            </Box>
          </ModalBody>
        )}
        <ModalFooter>
          <Button mr={3} onClick={handleClose} isDisabled={saving}>
            Cancelar
          </Button>
          <Button
            colorScheme="green"
            type="submit"
            form="opportunity-form"
            isLoading={saving}
            isDisabled={saving || (isEditing && !opportunity.data)}
          >
            {isEditing ? 'Salvar alterações' : 'Criar oportunidade'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default OpportunityFormModal;
